import React from 'react';
import { useParams } from 'react-router';
import { Alert, Group, Stack, Modal, Button, Loader, Center, Text } from '@mantine/core';
import { IconMessageCircle, IconMessageCircleOff } from '@tabler/icons-react';
import { useLocalStorage } from '@mantine/hooks';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { useSurfaceZIndex } from '@ayocore/platform/client/components/surfaceZIndex';
import { useUrlTabState } from '@ayocore/platform/client/hooks/useUrlTabState';
import { notifications } from '@mantine/notifications';
import { ViewLayout, ViewPanel } from '@ayocore/platform/client/components/EntityViewLayout';
import { CleanTabs } from '@ayocore/platform/client/components/CleanTabs';
import { usePartnerRepository, usePartnerRoleRepository } from '@ayocore/erp/crm/client/rpc';
import { useTab } from '@ayocore/platform/client/tabs';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';
import { CRM_ROLES } from '@ayocore/erp/crm/common/roles';
import { IDENTITY_ROLES } from '@ayocore/platform/identity/common/roles';
import { usePartnerViewController } from './usePartnerViewController';
import { EntityActivityPanel } from '@ayocore/activity/client/components/Activity/ui/embedded/EntityActivityPanel';
import { CRM_ACTIVITY_ENTITY_TYPES } from '@ayocore/activity/common/activity/constants';
import { CrmExtensions } from '@ayocore/erp/crm/client/extensions';
import { PartnerViewExtensions } from './PartnerViewExtensions';
import {
  PartnerHeader,
  PartnerPaymentsShipmentsChart,
  PartnerStats,
  PartnerPurchases,
  PartnerInfo,
  PartnerTasks,
  PartnerChannels,
  PartnerContactPersons,
  PartnerDeals,
  PartnerLeads,
  PartnerLeadFunnels,
  PartnerAttributesPanel,
} from './components';
import { MergePartnerForm } from './components/MergePartnerModal';
import { EntityFiles } from '@ayocore/platform/filemanager/client/components';
import { REPOSITORIES } from '@ayocore/platform/common/types/repositories.generated';
import type { User } from '@ayocore/platform/identity/schemas/user.schema';
import type { Segment } from '@ayocore/erp/crm/schemas/segment.schema';
import type { Tag } from '@ayocore/erp/crm/schemas/tag.schema';

interface PartnerViewProps {
  partnerId?: number;
  isModal?: boolean;
  showEventList?: boolean;
}

export function PartnerView({
  partnerId: propPartnerId,
  isModal = false,
  showEventList = true,
}: PartnerViewProps) {
  const { id } = useParams<{ id: string }>();

  // Use prop ID if provided, otherwise get from URL params
  const partnerId = propPartnerId || (id ? parseInt(id, 10) : null);

  if (!partnerId || isNaN(partnerId)) {
    return (
      <Alert color='red' title='Ошибка'>
        Неверный ID партнера
      </Alert>
    );
  }

  // Используем хук для получения состояния и экшенов
  const {
    partner,
    partnerStats,
    partnerChart,
    contactPersons,
    leads,
    deals,
    contactChannels,
    segments,
    tags,
    purchasesCount,
    loading,
    error,
    permissions,
    modals,
    openDelete,
    closeDelete,
    deletePartner,
    archivePartner,
    unarchivePartner,
    refreshContacts,
    refreshContactPersons,
    assignSegment,
    removeSegment,
    assignTag,
    removeTag,
    updatePartnerNotes,
    refreshPartner,
    refreshPermissions,
    reload,
  } = usePartnerViewController(partnerId, isModal);

  // Инлайн-модалка удаления (не через useModal): z-index из общего аллокатора,
  // иначе при открытии PartnerView в дравере модалка окажется под ним.
  const deleteModalZIndex = useSurfaceZIndex(modals.delete);

  const editPartnerModal = useModal(CrmExtensions.Partners.editForm.get());
  const partnerRepository = usePartnerRepository();
  const partnerRoleRepository = usePartnerRoleRepository();
  const { setTabTitle } = useTab();
  const [activeTab, setActiveTab] = useUrlTabState('partner_tab', 'main', { enabled: !isModal });
  const mergePartnerModal = useModal(MergePartnerForm);
  const userRoles = useAuthController(s => s.user?.roles ?? []);
  const canSeeAdditionalTab =
    userRoles.includes(CRM_ROLES.CRM_OWNER) || userRoles.includes(IDENTITY_ROLES.ADMIN);
  // Право на смену менеджера считает сервер (canUpdate(id, 'manager') в контроллере):
  // базовый CRM разрешает, ограничения навешивает инстанс своим guard'ом.
  // Persisted toggle для панели комментариев — должен быть до любых ранних return,
  // иначе React поймёт это как «больше хуков чем в прошлом рендере».
  const [activityPanelOpen, setActivityPanelOpen] = useLocalStorage<boolean>({
    key: 'crm.partnerView.activityPanelOpen',
    defaultValue: true,
  });

  const showDealsTab = !loading && (deals.length > 0 || leads.length > 0);
  const showPurchasesTab = purchasesCount !== null && purchasesCount > 0;

  const handleNotesSave = async (value: string) => {
    updatePartnerNotes(value);
    await partnerRepository.update(partnerId, { notes: value });
  };

  const handleSetSalesArea = async (salesAreaId: number | null) => {
    await partnerRepository.update(partnerId, { sales_area_id: salesAreaId });
    await refreshPartner();
  };

  const handleSetRoles = async (roleIds: number[]) => {
    await partnerRoleRepository.setPartnerRoles(partnerId, roleIds);
    await refreshPartner();
    // Точечные права инстанса могут зависеть от ролей партнёра (например, смена
    // менеджера только у клиентов в Grasias) — пересчитываем их после смены ролей.
    await refreshPermissions();
  };

  const handleAddressSave = async (value: string) => {
    await partnerRepository.update(partnerId, { address: value.trim() || null });
    await refreshPartner();
  };

  const handleNameSave = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    await partnerRepository.update(partnerId, { name: trimmed });
    await refreshPartner();
    setTabTitle(trimmed);
  };

  const handleFullNameSave = async (value: string) => {
    await partnerRepository.update(partnerId, { full_name: value.trim() || null });
    await refreshPartner();
  };

  const handleSetSegments = async (segmentIds: number[]) => {
    for (const s of segments) {
      await removeSegment(s.id);
    }
    for (const id of segmentIds) {
      await assignSegment(id);
    }
  };

  const handleSetTags = async (tagIds: number[]) => {
    for (const t of tags) {
      await removeTag(t.id);
    }
    for (const id of tagIds) {
      await assignTag(id);
    }
  };

  const handleManagerSave = async (managerId: number | null) => {
    await partnerRepository.update(partnerId, { manager_id: managerId });
    await refreshPartner();
  };

  if (loading) {
    return (
      <Center style={{ height: '100%', minHeight: 400 }}>
        <Loader size='lg' />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color='red' title='Ошибка'>
        {error}
      </Alert>
    );
  }

  if (!partner) {
    return (
      <Alert color='yellow' title='Не найден'>
        Партнер не найден
      </Alert>
    );
  }

  const handleArchive = async () => {
    const err = await archivePartner();
    if (err) notifications.show({ color: 'red', message: err });
  };

  const handleUnarchive = async () => {
    const err = await unarchivePartner();
    if (err) notifications.show({ color: 'red', message: err });
  };

  const handleDelete = async () => {
    const err = await deletePartner();
    if (err) notifications.show({ color: 'red', message: err });
  };

  const handleOpenEdit = () => {
    editPartnerModal.open(
      {
        title: 'Редактировать партнера',
        partnerId,
        onPartnerUpdated: () => {
          editPartnerModal.close();
          reload();
        },
      },
      { size: 'lg' }
    );
  };

  const handleOpenMerge = () => {
    mergePartnerModal.open(
      {
        title: 'Объединить с дублём',
        masterId: partnerId,
        onSuccess: () => {
          mergePartnerModal.close();
          notifications.show({ color: 'green', message: 'Партнёры объединены. Дубль удалён.' });
          reload();
        },
      },
      { size: 'md' }
    );
  };

  // Активные лиды (не архивные, статус не финальный) — для воронок и extension-слотов.
  // Семантика совпадает с серверным правилом lead_active_uniqueness_rules.
  const activeLeads = leads.filter(lead => !lead.is_archived && !lead.lead_status?.is_final);

  const extensionPayload = {
    partner,
    reload,
    canEdit: permissions.canEdit,
    activeLeads: activeLeads.map(lead => ({
      id: lead.id,
      title: lead.title,
      leadTypeSlug: lead.lead_type?.slug,
      leadTypeName: lead.lead_type?.name,
    })),
  };
  const extraTabs = PartnerViewExtensions.tabs
    .getAll()
    .map(fn => fn(extensionPayload))
    .filter((tab): tab is NonNullable<typeof tab> => tab !== null);
  const shouldHideEventList = PartnerViewExtensions.hideEventList
    .getAll()
    .some(fn => fn(extensionPayload));
  // Panel может быть полностью отключена пропсом/extension'ом — тогда кнопку тоже не показываем.
  const activityAllowed = showEventList && !shouldHideEventList;
  const effectiveShowEventList = activityAllowed && activityPanelOpen;

  // Ключи встроенных блоков главной вкладки, которые модули просят скрыть
  const hiddenMainBlocks = new Set(
    PartnerViewExtensions.hideMainPanelBlocks.getAll().flatMap(fn => fn(extensionPayload))
  );
  // Дополнительные блоки главной вкладки от клиентских модулей
  const extraMainBlocks = PartnerViewExtensions.mainPanelBlocks
    .getAll()
    .map(fn => fn(extensionPayload));
  // Дополнительные пункты меню действий от клиентских модулей
  const extraMenuItems = PartnerViewExtensions.menuItems.getAll().map(fn => fn(extensionPayload));
  // Дополнительные кнопки в action bar заголовка (вне выпадающего меню)
  const extraHeaderActions = PartnerViewExtensions.headerActions
    .getAll()
    .map(fn => fn(extensionPayload));

  const mainContent = (
    <>
      <PartnerHeader
        partner={partner}
        canEdit={permissions.canEdit}
        canDelete={permissions.canDelete}
        canArchive={permissions.canArchive}
        permissionsLoading={permissions.loading}
        onArchive={!partner.archived_at ? handleArchive : undefined}
        onUnarchive={partner.archived_at ? handleUnarchive : undefined}
        onEdit={handleOpenEdit}
        onDelete={openDelete}
        onMerge={!partner.linked_id && permissions.canMerge ? handleOpenMerge : undefined}
        onNameSave={handleNameSave}
        onFullNameSave={handleFullNameSave}
        extraMenuItems={extraMenuItems}
        extraHeaderActions={extraHeaderActions}
      />

      <CleanTabs value={activeTab} onChange={v => setActiveTab(v ?? 'main')}>
        <CleanTabs.List
          rightSection={
            activityAllowed ? (
              <Button
                size='compact-xs'
                variant='subtle'
                color='blue'
                leftSection={
                  activityPanelOpen ? (
                    <IconMessageCircleOff size={14} />
                  ) : (
                    <IconMessageCircle size={14} />
                  )
                }
                onClick={() => setActivityPanelOpen(prev => !prev)}
              >
                {activityPanelOpen ? 'Скрыть комментарии' : 'Показать комментарии'}
              </Button>
            ) : undefined
          }
        >
          <CleanTabs.Tab value='main'>Основное</CleanTabs.Tab>
          <CleanTabs.Tab value='contacts'>Контактные лица</CleanTabs.Tab>
          {showDealsTab && <CleanTabs.Tab value='deals'>Сделки</CleanTabs.Tab>}
          {showPurchasesTab && <CleanTabs.Tab value='purchases'>Закупки</CleanTabs.Tab>}
          <CleanTabs.Tab value='files'>Файлы</CleanTabs.Tab>
          <CleanTabs.Tab value='analytics'>Аналитика</CleanTabs.Tab>
          {extraTabs.map(tab => (
            <CleanTabs.Tab key={tab.value} value={tab.value}>
              {tab.label}
            </CleanTabs.Tab>
          ))}
          {canSeeAdditionalTab && <CleanTabs.Tab value='additional'>Дополнительно</CleanTabs.Tab>}
        </CleanTabs.List>

        <CleanTabs.Panel value='main'>
          <Stack gap='xs'>
            {!hiddenMainBlocks.has('leadFunnels') && (
              <PartnerLeadFunnels activeLeads={activeLeads} onRefresh={reload} />
            )}

            {!hiddenMainBlocks.has('info') && (
              <PartnerInfo
                partner={partner}
                segments={segments as Segment[]}
                tags={tags as Tag[]}
                canEdit={permissions.canEdit}
                canEditManager={permissions.canEditManager}
                onNotesSave={handleNotesSave}
                onAddressSave={handleAddressSave}
                onSetSalesArea={handleSetSalesArea}
                onSetSegments={handleSetSegments}
                onSetTags={handleSetTags}
                onSetRoles={handleSetRoles}
                onManagerSave={handleManagerSave}
              />
            )}

            {!hiddenMainBlocks.has('tasks') && <PartnerTasks partnerUuid={partner.uuid} />}

            {!hiddenMainBlocks.has('channels') && (
              <PartnerChannels
                entityUuid={partner.uuid}
                contactChannels={contactChannels}
                onChannelsChanged={refreshContacts}
              />
            )}

            {!hiddenMainBlocks.has('contactPersons') && (
              <PartnerContactPersons
                entityId={partnerId}
                entityType='crm::PartnerRepository'
                contactPersons={contactPersons}
                onContactPersonsChanged={refreshContactPersons}
              />
            )}

            {!hiddenMainBlocks.has('meta') && (
              <ViewPanel>
                <Group gap='lg'>
                  <Text size='xs' c='dimmed'>
                    ID: {partner.id}
                  </Text>
                  <Text size='xs' c='dimmed'>
                    Создан: {new Date(partner.created_at).toLocaleDateString()}
                  </Text>
                  <Text size='xs' c='dimmed'>
                    Обновлен: {new Date(partner.updated_at).toLocaleDateString()}
                  </Text>
                  {(partner.manager as User) && (
                    <Text size='xs' c='dimmed'>
                      Менеджер: {`${(partner.manager as User)?.name || 'Неизвестно'}`}
                    </Text>
                  )}
                </Group>
              </ViewPanel>
            )}

            {extraMainBlocks.map((node, idx) => (
              <React.Fragment key={idx}>{node}</React.Fragment>
            ))}
          </Stack>
        </CleanTabs.Panel>

        <CleanTabs.Panel value='contacts'>
          <PartnerContactPersons
            entityId={partnerId}
            entityType='crm::PartnerRepository'
            contactPersons={contactPersons}
            onContactPersonsChanged={refreshContactPersons}
            showAll
            showSearch
          />
        </CleanTabs.Panel>

        <CleanTabs.Panel value='deals'>
          <Stack gap='xs'>
            <PartnerDeals deals={deals} />
            <PartnerLeads leads={leads} />
          </Stack>
        </CleanTabs.Panel>

        <CleanTabs.Panel value='purchases'>
          <PartnerPurchases partnerId={partnerId} />
        </CleanTabs.Panel>

        <CleanTabs.Panel value='files'>
          <EntityFiles
            entityUuid={partner.uuid}
            entityType={REPOSITORIES.CRM_PARTNER}
            directory='partners'
          />
        </CleanTabs.Panel>

        <CleanTabs.Panel value='analytics'>
          <Stack gap='xs'>
            <PartnerStats stats={partnerStats} />
            <PartnerPaymentsShipmentsChart data={partnerChart} />
          </Stack>
        </CleanTabs.Panel>

        {extraTabs.map(tab => (
          <CleanTabs.Panel key={tab.value} value={tab.value}>
            {tab.content}
          </CleanTabs.Panel>
        ))}

        {canSeeAdditionalTab && (
          <CleanTabs.Panel value='additional'>
            <PartnerAttributesPanel partnerId={partnerId} />
          </CleanTabs.Panel>
        )}
      </CleanTabs>
    </>
  );

  return (
    <>
      <ViewLayout
        storageKey={effectiveShowEventList ? 'crm.partnerView.leftPanelPercent' : undefined}
      >
        <ViewLayout.Stack>{mainContent}</ViewLayout.Stack>
        {effectiveShowEventList && (
          <ViewLayout.Stack>
            <EntityActivityPanel
              entityType={CRM_ACTIVITY_ENTITY_TYPES.PARTNER}
              entityUuid={partner.uuid}
            />
          </ViewLayout.Stack>
        )}
      </ViewLayout>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={modals.delete}
        onClose={closeDelete}
        title='Удалить партнера'
        size='sm'
        zIndex={deleteModalZIndex}
      >
        <Stack gap='md'>
          <Text>
            Вы уверены, что хотите удалить партнера <strong>"{partner?.name}"</strong>?
          </Text>
          <Text size='sm' c='dimmed'>
            Это действие нельзя отменить.
          </Text>
          <Group justify='flex-end' gap='sm'>
            <Button variant='light' onClick={closeDelete} color='gray' size='xs'>
              Отмена
            </Button>
            <Button variant='filled' onClick={handleDelete} color='red' size='xs'>
              Удалить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
